/* istanbul ignore file */
import { FILTER_BY } from '../consts';
import { Separator } from 'components/base/layout-components';
import Toggle from 'components/base/Toggle';
import ReposAutocomplete from './ReposAutocomplete';
import ServicesAutocomplete from './ServicesAutocomplete';
import TreeTeamsDropdown from 'components/teams/TreeTeamsDropdown/TreeTeamsDropdown';
import * as TrackingService from 'common/services/tracking.service';
import { DASHBOARD_INTERNAL_TYPES, TRACKING } from 'common/constants/constants';
import ContributorsAutocomplete from './ContributorsAutocomplete';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import {
  isTeam,
  isTeamByLicense,
  isMulti,
  navigateBookDemoClick
} from 'common/utils/utils';
import _ from 'lodash';
import LabelsAutocomplete from './LabelsAutocomplete';
import { flattenTree } from 'components/base/MultiLevelDropdown/MultiLevelDropdown.utils';
import store from 'state/store/configureStore';
import {
  selectedTeamWithExtraDataSelector,
  isSelectedTeamParentSelector
} from 'state/selectors/teams';
import { ALL_TEAMS } from 'common/constants/teams';
import { BookDemoTooltip } from '@linearb/linui-common';
import {
  CTA_TOOLTIP_TEXTS,
  TRACKING as TRACKING_EVENTS
} from '../../../../dashboard/doraV2/dashboard.doraV2.freeTier.consts';

/* eslint-disable-next-line */
export const getTeamLevelFilters = ({
  filters,
  getSettingOrDefinitionValue,
  metricsFilterByServiceEnabled,
  license,
  isShowTeamDropdown,
  shouldShowTeamOrRepoDropdowns,
  shouldShowLabelsDropdown,
  isShowToggle,
  enableRepositoryLevelMetricsSetting,
  isShowReposDropdown,
  isShowContributorDropdown,
  services,
  isTeamLicense,
  isMultiTeamsAllowed,
  isCustom,
  isShowAllTeams,
  defaultTeam,
  currentTeams,
  repositories,
  isPmCustomDashboard,
  report,
  pullRequestLabels,
  isUncheckDefault,
  setIsUncheckDefault,
  enableTeamSelection,
  isAllControbutorsTeamSelected
}) => {
  const { defaultSelectedTeam, globallySelectedTeams: filtersSelectedTeams } =
    filters;
  const globalSelectedTeam = selectedTeamWithExtraDataSelector(
    store.getState()
  );
  const isTeamGitSettingEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isTeamGitSettingEnabled
  });
  const isDefaultReport = isCustom && !isPmCustomDashboard;

  const isTeamGoalsReport =
    _.get(report, 'internal_type', '') === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS;

  const isMultiCriteriaFiltering =
    !isTeamGoalsReport &&
    isTeamGitSettingEnabled &&
    isTeam(globalSelectedTeam?.[0]) &&
    filtersSelectedTeams?.length === 1 &&
    isTeam(filtersSelectedTeams?.[0]);

  function isFilterType(type) {
    return isMultiCriteriaFiltering || filters.filterType === type;
  }

  const multiCriteriaFilterWidth = isMultiCriteriaFiltering
    ? { width: '15rem' }
    : {};

  const isSelectedTeamParent = isSelectedTeamParentSelector(store.getState());
  const globalTeamsScope = flattenTree(globalSelectedTeam, 'children');
  const globalTeamsIds = globalTeamsScope.map(team => team?.id).filter(Boolean);

  const isAllTeamsSelected = defaultSelectedTeam?.[0] === ALL_TEAMS.id;

  const shouldShowFilterByServices =
    metricsFilterByServiceEnabled && !isTeamByLicense(license);

  const FilterByOptions = [
    FILTER_BY.PEOPLE,
    ...(shouldShowFilterByServices ? [FILTER_BY.SERVICE] : []),
    FILTER_BY.REPO,
    FILTER_BY.LABEL
  ];

  const shouldShowServicesDropdown = isFilterType(FILTER_BY.SERVICE);

  const shouldShowDefaultDropdown =
    (!enableRepositoryLevelMetricsSetting || isFilterType(FILTER_BY.PEOPLE)) &&
    isShowTeamDropdown;

  const shouldShowContributorDropdown =
    isShowContributorDropdown &&
    shouldShowDefaultDropdown &&
    !isMulti(filters.globallySelectedTeams);

  const getSelectedTeamsIds = () => {
    const globallySelectedTeams = filters.globallySelectedTeams || [];
    const isCustomAllSelected =
      _.get(globallySelectedTeams, '[0]') === ALL_TEAMS.id ||
      _.isEmpty(_.get(globallySelectedTeams, '[0]'));
    if (isCustomAllSelected) {
      return [ALL_TEAMS.id];
    }
    return globallySelectedTeams.map(({ id }) => id);
  };

  const filtersForTeamLevel = [];

  const addLeadingSeparatorIfNeeded = () => {
    if (shouldShowTeamOrRepoDropdowns) {
      filtersForTeamLevel.push(<Separator key='Separator' height='2rem' />);
    }
  };

  const addFilterByTogglerIfNeeded = () => {
    if (isMultiCriteriaFiltering) {
      return;
    }

    if (isShowToggle && enableRepositoryLevelMetricsSetting) {
      filtersForTeamLevel.push(
        <Toggle
          key='FilterByToggle'
          title='Filter by: '
          value={filters.filterType}
          onChange={(event, newFilterType) => {
            if (newFilterType !== null) {
              filters.setFilterType(newFilterType);
              if (newFilterType === FILTER_BY.REPO) {
                filters.setGloballySelectedTeams(defaultSelectedTeam);
                filters.setSelectedContributor(null);
                filters.setSelectedService([]);
                filters.setSelectedLabels([]);
              } else if (newFilterType === FILTER_BY.SERVICE) {
                filters.setGloballySelectedTeams(defaultSelectedTeam);
                filters.setSelectedContributor(null);
                filters.setSelectedRepo([]);
                filters.setSelectedLabels([]);
              } else if (newFilterType === FILTER_BY.LABEL) {
                filters.setGloballySelectedTeams(defaultSelectedTeam);
                filters.setSelectedContributor(null);
                filters.setSelectedRepo([]);
                filters.setSelectedService([]);
              } else {
                filters.setGloballySelectedTeams(defaultSelectedTeam);
                filters.setSelectedRepo([]);
                filters.setSelectedService([]);
                filters.setSelectedLabels([]);
              }
            }
          }}
          options={FilterByOptions}
        />
      );
    }
  };

  const addRepoAutoCompleteIfNeeded = () => {
    if (
      enableRepositoryLevelMetricsSetting &&
      isShowReposDropdown &&
      isFilterType(FILTER_BY.REPO)
    ) {
      filtersForTeamLevel.push(
        <ReposAutocomplete
          selectedRepo={filters.selectedRepo}
          repositories={repositories}
          isCustom={isCustom}
          key='ReposAutocomplete'
          onChange={repo => {
            filters.setSelectedRepo(repo);
            if (!isMultiCriteriaFiltering) {
              filters.setSelectedLabels([]);
              filters.setGloballySelectedTeams(defaultSelectedTeam);
              filters.setSelectedContributor(null);
              filters.setSelectedService([]);
            }
          }}
          {...(isMultiCriteriaFiltering ? { width: '16.1rem' } : {})}
        />
      );
    }
  };

  const addLabelsAutoCompleteIfNeeded = () => {
    if (shouldShowLabelsDropdown && isFilterType(FILTER_BY.LABEL)) {
      filtersForTeamLevel.push(
        <LabelsAutocomplete
          selectedLabels={filters.selectedLabels}
          labels={pullRequestLabels}
          multipleLimit={3}
          key='LabelsAutocomplete'
          onChange={labels => {
            filters.setSelectedLabels(labels);
            if (!isMultiCriteriaFiltering) {
              filters.setSelectedRepo([]);
              filters.setGloballySelectedTeams(defaultSelectedTeam);
              filters.setSelectedContributor(null);
              filters.setSelectedService([]);
            }
          }}
          {...multiCriteriaFilterWidth}
        />
      );
    }
  };

  const addServicesAutoCompleteIfNeeded = () => {
    if (shouldShowServicesDropdown) {
      filtersForTeamLevel.push(
        <ServicesAutocomplete
          key='ServicesAutocomplete'
          services={services}
          selectedService={filters.selectedService}
          onChange={service => {
            filters.setSelectedService(service);
            if (!isMultiCriteriaFiltering) {
              filters.setSelectedLabels([]);
              filters.setSelectedRepo([]);
              filters.setGloballySelectedTeams(defaultSelectedTeam);
              filters.setSelectedContributor(null);
            }
          }}
          {...multiCriteriaFilterWidth}
        />
      );
    }
  };

  const addTreeTeamsDropDownIfNeeded = ({ force = false } = {}) => {
    if (
      (isMultiCriteriaFiltering && !isDefaultReport) ||
      (!shouldShowDefaultDropdown && !force)
    ) {
      return;
    }

    const disableBookDemo = !isTeamLicense || !isAllControbutorsTeamSelected;

    const clickAndTrackBookDemo = () => {
      TrackingService.logEvent(TRACKING_EVENTS.EVENTS.CTA_CLICK, {
        page: TRACKING.PAGES.METRICS,
        cta: TRACKING_EVENTS.CTA_TYPE.DROPDOWN,
        intent: TRACKING_EVENTS.CTA_INTENT.BOOK_A_DEMO,
        source_url: window.location.href
      });
      navigateBookDemoClick();
    };

    const bookDemoTooltipProps = {
      message: CTA_TOOLTIP_TEXTS.METRICS_TEAMS,
      buttonText: CTA_TOOLTIP_TEXTS.BOOK_A_DEMO,
      disabled: disableBookDemo,
      onClick: clickAndTrackBookDemo
    };

    const teamsDropdown = (
      <TreeTeamsDropdown
        key='TeamsDropdown'
        disabledDropdown={!enableTeamSelection}
        data-testid='multilevel-dropdown'
        selectedTeamIds={getSelectedTeamsIds()}
        multiple={isMultiTeamsAllowed && !isTeamLicense}
        multipleLimit={10}
        onChange={selectedTeams => {
          setIsUncheckDefault(!selectedTeams.length);
          TrackingService.logEvent(TRACKING.EVENTS.FILTER, {
            page: TRACKING.PAGES.METRICS,
            filter_name: TRACKING.FILTER.TEAM,
            ...(isCustom
              ? { page_tab: TRACKING.PAGE_TABS.CUSTOM_DASHBOARD }
              : {}),
            team_names: selectedTeams.map(({ name }) => name || '').join(', ')
          });
          filters.setSelectedRepo([]);
          filters.setSelectedLabels([]);
          filters.setSelectedService([]);
          filters.setSelectedContributor(null);
          if (!isAllTeamsSelected) {
            const contextSelectedTeams = selectedTeams.filter(team =>
              globalTeamsIds.includes(team?.id)
            );
            filters.setGloballySelectedTeams(
              contextSelectedTeams?.length === 0
                ? globalSelectedTeam
                : contextSelectedTeams
            );
          } else {
            filters.setGloballySelectedTeams(selectedTeams);
          }
        }}
        width='16rem'
        menuWidth='25rem'
        menuWidthExpanded='50rem'
        numOfMenus={2}
        isShowAllTeams={isShowAllTeams && !isTeamLicense}
        customAllItem={ALL_TEAMS}
        isFilterTreeBySelectedTeam={!isAllTeamsSelected}
        isUncheckDefault={isSelectedTeamParent && isUncheckDefault}
      />
    );

    const teamsDropdownFilter = disableBookDemo ? (
      teamsDropdown
    ) : (
      <BookDemoTooltip {...bookDemoTooltipProps}>
        <div>{teamsDropdown}</div>
      </BookDemoTooltip>
    );

    filtersForTeamLevel.push(teamsDropdownFilter);
  };

  const addContributorsAutoCompleteIfNeeded = () => {
    if (shouldShowContributorDropdown) {
      filtersForTeamLevel.push(
        <ContributorsAutocomplete
          key='MemebersAutocomplete'
          selectedContributor={filters.selectedContributor}
          globallySelectedTeams={filters.globallySelectedTeams}
          onChange={contributor => {
            filters.setSelectedContributor(contributor ? contributor : null);
            if (!isMultiCriteriaFiltering) {
              filters.setSelectedRepo([]);
              filters.setSelectedLabels([]);
              filters.setSelectedService([]);
            }
          }}
          isTeamLicense={isTeamLicense}
        />
      );
    }
  };

  if (isPmCustomDashboard) {
    return [];
  }
  if (isTeamGoalsReport || isAllControbutorsTeamSelected) {
    addTreeTeamsDropDownIfNeeded({ force: true });
    return filtersForTeamLevel;
  }

  addLeadingSeparatorIfNeeded();
  addFilterByTogglerIfNeeded();
  addTreeTeamsDropDownIfNeeded();
  addContributorsAutoCompleteIfNeeded();
  addServicesAutoCompleteIfNeeded();
  addRepoAutoCompleteIfNeeded();
  addLabelsAutoCompleteIfNeeded();
  return filtersForTeamLevel;
};
